import { Box, Stack, Typography } from '@mui/material'
import { EmphasizedNumber } from 'utoniq-ui'

import {
  ConsolePreSaleDetailFragment,
  PreSaleStatus,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type PreSaleContentCellProps = {
  preSale: ConsolePreSaleDetailFragment
}

export const PreSaleApplicantsCell: Cell<PreSaleContentCellProps> = ({
  preSale,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')

  // 受付前は表示しない
  if (preSale.preSaleStatus === PreSaleStatus.BeforeReceiption) return null

  const isLottery = preSale.type === PreSaleType.Lottery
  // 抽選の当落処理前かどうか
  const isBeforeLottery =
    isLottery &&
    (preSale.preSaleStatus === PreSaleStatus.Accepting ||
      preSale.preSaleStatus === PreSaleStatus.AcceptingLottery)

  // クレジットカードでの応募・支払い状況のカウント
  const creditCardAggregation = {
    failedCount: 0,
    ...(isLottery
      ? preSale.lottery?.applicantAggregation?.creditCard
      : {
          ...preSale.firstComeFirstServed?.applicantAggregation?.creditCard,
          appliedQuantity: 0,
          purchasedQuantity: 0,
          failedCount: 0,
          failedQuantity: 0,
        }),
  }
  // コンビニ決済での応募・支払い状況のカウント
  const konbiniAggregation = isLottery
    ? preSale.lottery?.applicantAggregation?.konbini
    : {
        ...preSale.firstComeFirstServed?.applicantAggregation?.konbini,
        appliedQuantity: 0,
        purchasedQuantity: 0,
        unpaidQuantity: 0,
      }

  // 応募者全体の応募
  const applicants = {
    count:
      (creditCardAggregation?.appliedCount ?? 0) +
      (konbiniAggregation?.appliedCount ?? 0),
    quantity:
      (creditCardAggregation?.appliedQuantity ?? 0) +
      (konbiniAggregation?.appliedQuantity ?? 0),
    details: [
      {
        label: t('credit_card', 'クレジットカード決済'),
        count: creditCardAggregation?.appliedCount ?? 0,
        quantity: creditCardAggregation?.appliedQuantity ?? 0,
      },
      {
        label: t('konbini', 'コンビニ決済'),
        count: konbiniAggregation?.appliedCount ?? 0,
        quantity: konbiniAggregation?.appliedQuantity ?? 0,
      },
    ],
  }

  // 当選者の支払い状況。抽選前の段階では "-" と表示
  const winners = {
    count: isBeforeLottery
      ? '-'
      : (creditCardAggregation?.purchasedCount ?? 0) +
        (creditCardAggregation?.failedCount ?? 0) +
        (konbiniAggregation?.purchasedCount ?? 0) +
        (konbiniAggregation?.unpaidCount ?? 0),
    quantity: isBeforeLottery
      ? '-'
      : (creditCardAggregation?.purchasedQuantity ?? 0) +
        (creditCardAggregation?.failedQuantity ?? 0) +
        (konbiniAggregation?.purchasedQuantity ?? 0) +
        (konbiniAggregation?.unpaidQuantity ?? 0),
    details: [
      {
        label: t('creditCardPaymentStatus', 'クレジットカード決済入金状況'),
        count: isBeforeLottery
          ? '-'
          : (creditCardAggregation?.purchasedCount ?? 0) +
            (creditCardAggregation?.failedCount ?? 0),
        quantity: isBeforeLottery
          ? '-'
          : (creditCardAggregation?.purchasedQuantity ?? 0) +
            (creditCardAggregation?.failedQuantity ?? 0),
        items: [
          {
            status: t('purchased', '入金済み'),
            count: isBeforeLottery
              ? '-'
              : creditCardAggregation?.purchasedCount ?? 0,
            quantity: isBeforeLottery
              ? '-'
              : creditCardAggregation?.purchasedQuantity ?? 0,
          },
          ...(isLottery
            ? [
                {
                  status: t('failed', '決済失敗'),
                  count: isBeforeLottery
                    ? '-'
                    : creditCardAggregation?.failedCount ?? 0,
                  quantity: isBeforeLottery
                    ? '-'
                    : creditCardAggregation?.failedQuantity ?? 0,
                },
              ]
            : []),
        ],
      },
      {
        label: t('konbiniPaymentStatus', 'コンビニ決済入金状況'),
        count: isBeforeLottery
          ? '-'
          : (konbiniAggregation?.purchasedCount ?? 0) +
            (konbiniAggregation?.unpaidCount ?? 0),
        quantity: isBeforeLottery
          ? '-'
          : (konbiniAggregation?.purchasedQuantity ?? 0) +
            (konbiniAggregation?.unpaidQuantity ?? 0),
        items: [
          {
            status: t('purchased', '入金済み'),
            count: isBeforeLottery
              ? '-'
              : konbiniAggregation?.purchasedCount ?? 0,
            quantity: isBeforeLottery
              ? '-'
              : konbiniAggregation?.purchasedQuantity ?? 0,
          },
          {
            status: t('notPurchased', '未入金'),
            count: isBeforeLottery ? '-' : konbiniAggregation?.unpaidCount ?? 0,
            quantity: isBeforeLottery
              ? '-'
              : konbiniAggregation?.unpaidQuantity ?? 0,
          },
        ],
      },
    ],
  }

  return (
    <CellFrame sx={{ pt: 3, pb: 5 }}>
      <Typography variant="h4" sx={{ pt: 1, pb: 2 }}>
        {isLottery
          ? t('applicantStatus', '応募/当選状況')
          : t('purchaseStatus', '購入状況')}
      </Typography>

      <Stack
        sx={{
          borderStyle: 'solid',
          borderRadius: '8px',
          borderWidth: 1,
          borderColor: 'grey.100',
          p: 5,
        }}
      >
        {isLottery && (
          <>
            <Typography variant="h6">
              {t('applicantsCount', '応募枚数/応募者数')}
            </Typography>
            <Stack
              sx={{ flexDirection: 'row', alignItems: 'baseline', gap: 1 }}
            >
              <EmphasizedNumber variant="h1" sx={{ fontFamily: 'Oswald' }}>
                {applicants.quantity}
              </EmphasizedNumber>
              <Typography variant="h6">{t('ticket', '枚')}</Typography>
              <Typography variant="h1" sx={{ fontFamily: 'Oswald' }}>
                {' / '}
              </Typography>
              <EmphasizedNumber variant="h1" sx={{ fontFamily: 'Oswald' }}>
                {applicants.count}
              </EmphasizedNumber>
              <Typography variant="h6">{t('people', '人')}</Typography>
            </Stack>
            <Box sx={{ mt: 2 }}>
              {applicants.details.map(({ label, count, quantity }) => {
                return (
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: -1,
                    }}
                  >
                    <Typography variant="caption">{label}</Typography>
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '2px',
                      }}
                    >
                      <EmphasizedNumber
                        variant="h6"
                        sx={{ fontFamily: 'Oswald' }}
                      >
                        {quantity}
                      </EmphasizedNumber>
                      <Typography variant="subtitle2">
                        {t('ticket', '枚')}
                      </Typography>
                      <Typography variant="subtitle2"> / </Typography>
                      <EmphasizedNumber variant="subtitle2">
                        {count}
                      </EmphasizedNumber>
                      <Typography variant="subtitle2">
                        {t('people', '人')}
                      </Typography>
                    </Stack>
                  </Stack>
                )
              })}
            </Box>

            <Box
              sx={{
                height: '1px',
                bgcolor: 'grey.100',
                mt: 3,
              }}
            />
          </>
        )}

        <Box className="winners" sx={{ mt: 3 }}>
          <Typography variant="h6">
            {isLottery
              ? t('winnersCount', '当選枚数/当選者数')
              : t('purchaseCount', '購入者数')}
          </Typography>
          <Stack sx={{ flexDirection: 'row', alignItems: 'baseline', gap: 1 }}>
            {isLottery && (
              <>
                <EmphasizedNumber variant="h1" sx={{ fontFamily: 'Oswald' }}>
                  {winners.quantity}
                </EmphasizedNumber>
                <Typography variant="h6">{t('ticket', '枚')}</Typography>
                <Typography variant="h1" sx={{ fontFamily: 'Oswald' }}>
                  {' / '}
                </Typography>
              </>
            )}
            <EmphasizedNumber variant="h1" sx={{ fontFamily: 'Oswald' }}>
              {winners.count}
            </EmphasizedNumber>
            <Typography variant="h6">{t('people', '人')}</Typography>
          </Stack>

          {winners.details.map(({ label, count, quantity, items }) => (
            <Box key={label} sx={{ mt: 2 }}>
              <Stack
                direction="row"
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2">{label}</Typography>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  sx={{ gap: '2px' }}
                >
                  {isLottery && (
                    <>
                      <EmphasizedNumber variant="overline">
                        {quantity}
                      </EmphasizedNumber>
                      <Typography variant="overline">
                        {t('ticket', '枚')}
                      </Typography>
                      <Typography variant="overline"> / </Typography>
                    </>
                  )}
                  <EmphasizedNumber variant="overline">
                    {count}
                  </EmphasizedNumber>
                  <Typography variant="overline">
                    {t('people', '人')}
                  </Typography>
                </Stack>
              </Stack>
              {items.map(({ status, count, quantity }) => (
                <Stack
                  key={status}
                  sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 0,
                  }}
                >
                  <Typography variant="caption">{status}</Typography>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'baseline',
                      gap: '2px',
                    }}
                  >
                    {isLottery && (
                      <>
                        <EmphasizedNumber variant="overline">
                          {quantity}
                        </EmphasizedNumber>
                        <Typography variant="overline">
                          {t('ticket', '枚')}
                        </Typography>
                        <Typography variant="overline"> / </Typography>
                      </>
                    )}
                    <EmphasizedNumber variant="overline">
                      {count}
                    </EmphasizedNumber>
                    <Typography variant="overline">
                      {t('people', '人')}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Box>
          ))}
        </Box>
      </Stack>
    </CellFrame>
  )
}
